import React, { createContext, useContext, useState, useEffect } from 'react';
import axios from 'axios';

const CartContext = createContext();

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export const useCart = () => {
  const context = useContext(CartContext);
  if (!context) {
    throw new Error('useCart must be used within CartProvider');
  }
  return context;
};

export const CartProvider = ({ children }) => {
  const [cart, setCart] = useState([]);
  const [sessionId, setSessionId] = useState('');
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    let sid = localStorage.getItem('session_id');
    if (!sid) {
      sid = `session_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
      localStorage.setItem('session_id', sid);
    }
    setSessionId(sid);
    loadCart(sid);
  }, []);

  const loadCart = async (sid) => {
    try {
      setLoading(true);
      const response = await axios.get(`${API}/cart/${sid}`);
      setCart(response.data);
    } catch (error) {
      console.error('Error loading cart:', error);
    } finally {
      setLoading(false);
    }
  };

  const addToCart = async (itemType, itemId) => {
    try {
      setLoading(true);
      await axios.post(`${API}/cart`, {
        session_id: sessionId,
        item_type: itemType,
        item_id: itemId,
      });
      await loadCart(sessionId);
      return true;
    } catch (error) {
      console.error('Error adding to cart:', error);
      throw error;
    } finally {
      setLoading(false);
    }
  };

  const removeFromCart = async (itemId) => {
    try {
      setLoading(true);
      await axios.delete(`${API}/cart/${sessionId}/${itemId}`);
      await loadCart(sessionId);
    } catch (error) {
      console.error('Error removing from cart:', error);
    } finally {
      setLoading(false);
    }
  };

  const clearCart = async () => {
    try {
      setLoading(true);
      await axios.delete(`${API}/cart/${sessionId}`);
      setCart([]);
    } catch (error) {
      console.error('Error clearing cart:', error);
    } finally {
      setLoading(false);
    }
  };

  const getCartTotal = () => {
    return cart.reduce((total, item) => total + item.price, 0);
  };

  const isInCart = (itemId) => {
    return cart.some(item => item.item_id === itemId);
  };

  return (
    <CartContext.Provider
      value={{
        cart,
        sessionId,
        loading,
        addToCart,
        removeFromCart,
        clearCart,
        getCartTotal,
        isInCart,
        refreshCart: () => loadCart(sessionId),
      }}
    >
      {children}
    </CartContext.Provider>
  );
};